// Shape of assistants.configuration.knowledge_base.providers[].
// Shared contract with voxi-2-0 (which builds one search tool per collection); keep
// VOYAGE_EMBEDDING_MODELS and the field shapes identical in both repos.

export const VOYAGE_EMBEDDING_MODELS = [
  "voyage-4-large",
  "voyage-4",
  "voyage-4-lite",
  "voyage-3-large",
  "voyage-3.5",
  "voyage-3.5-lite",
  "voyage-code-3",
  "voyage-finance-2",
  "voyage-law-2",
] as const;

export type VoyageEmbeddingModel = (typeof VOYAGE_EMBEDDING_MODELS)[number];

export const DEFAULT_VOYAGE_EMBEDDING_MODEL: VoyageEmbeddingModel = "voyage-4";

// Rerank models, checked 2026-10-30. docs.voyageai.com/reference/reranker-api (the request
// spec) names rerank-2.5 / rerank-2.5-lite as the recommended models and lists the older ones.
// The guide page docs.voyageai.com/docs/reranker instead calls rerank-3 / rerank-3-lite current
// and everything else legacy; rerank-3 is absent from the API spec, so it is left out here.
// Keep identical to voxi-2-0.
export const VOYAGE_RERANK_MODELS = [
  { id: "rerank-2.5", label: "rerank-2.5" },
  { id: "rerank-2.5-lite", label: "rerank-2.5-lite" },
  { id: "rerank-2", label: "rerank-2 (legacy)" },
  { id: "rerank-2-lite", label: "rerank-2-lite (legacy)" },
  { id: "rerank-1", label: "rerank-1 (legacy)" },
  { id: "rerank-lite-1", label: "rerank-lite-1 (legacy)" },
] as const;

export type VoyageRerankModel = (typeof VOYAGE_RERANK_MODELS)[number]["id"];

export const DEFAULT_VOYAGE_RERANK_MODEL: VoyageRerankModel = "rerank-2.5";

/**
 * Vector sizes each model can produce (verified against docs.voyageai.com/docs/embeddings,
 * 2026-10-30). The first entry is the model's default size. Models with one entry have no
 * output_dimension option.
 */
export const VOYAGE_MODEL_DIMENSIONS: Record<VoyageEmbeddingModel, number[]> = {
  "voyage-4-large": [1024, 256, 512, 2048],
  "voyage-4": [1024, 256, 512, 2048],
  "voyage-4-lite": [1024, 256, 512, 2048],
  "voyage-3-large": [1024, 256, 512, 2048],
  "voyage-3.5": [1024, 256, 512, 2048],
  "voyage-3.5-lite": [1024, 256, 512, 2048],
  "voyage-code-3": [1024, 256, 512, 2048],
  "voyage-finance-2": [1024],
  "voyage-law-2": [1024],
};

export type KnowledgeFieldType =
  | "keyword"
  | "integer"
  | "float"
  | "bool"
  | "text"
  | "datetime"
  | "geo"
  | "uuid";

export interface KnowledgeFilterableField {
  name: string;
  type: KnowledgeFieldType;
}

export interface KnowledgePayloadField {
  name: string;
  type: KnowledgeFieldType | string;
  indexed: boolean;
}

export interface KnowledgeCollectionSchema {
  points_count: number;
  vector_size: number | null;
  /** Per-named-vector sizes; set only for named-vector collections. */
  named_vectors?: Record<string, number>;
  distance: string;
  payload_fields: KnowledgePayloadField[];
  captured_at: string;
}

export interface KnowledgeQdrantCollection {
  id: string;
  enabled: boolean;
  collection: string;
  vector_name: string | null;
  embedding_model: VoyageEmbeddingModel;
  tool_name: string;
  description: string;
  max_results: number;
  score_threshold: number | null;
  rerank: boolean;
  /** Used only when rerank is true. Absent on collections saved before this field existed. */
  rerank_model?: VoyageRerankModel;
  returned_fields: string[];
  filterable_fields: KnowledgeFilterableField[];
  schema: KnowledgeCollectionSchema;
}

export interface KnowledgeBuiltinProvider {
  id: string;
  type: "builtin";
  enabled: boolean;
}

export interface KnowledgeQdrantProvider {
  id: string;
  type: "qdrant";
  enabled: boolean;
  collections: KnowledgeQdrantCollection[];
}

export type KnowledgeProvider = KnowledgeBuiltinProvider | KnowledgeQdrantProvider;
